import React from 'react';
import {
  newformula_facts_9P,
  newformula_card_labels,
  thai_sku_code,
  thai_units,
  thai_other_ingredients,
  thai_serving_size,
  thai_active_strain
} from '../../configs/supplement/9P_newformula_facts';

// Reference base width the font sizes below are tuned for. The card scales
// linearly with the `width` prop so it stays proportional inside the grid.
const BASE_WIDTH = 240;

// Font family that renders both Latin and Thai glyphs (matches the rest of the
// PDF pages).
const CARD_FONT = '"Gilroy-Regular", "Gilroy", "Noto Sans Thai", "Noto Sans", system-ui, sans-serif';

// ---- Product stick image, tilted over the card's top-right corner ----
// Artwork lives at public/supplement/newformula/<SKU>.png: a horizontal stick
// sachet centred on a square canvas with generous transparent padding, so the
// box has to be much wider than the stick itself appears.
// If a SKU has no image the <img> onError hides it and the header renders
// normally.
// ---- TUNING ----
// * ROTATION -> tilt in degrees; negative lifts the right-hand end.
// * WIDTH    -> box width at BASE_WIDTH (the stick fills ~74% of it).
// * TOP      -> negative values push the stick above the card's top edge.
// * RIGHT    -> negative values push it past the card's right edge.
const PRODUCT_IMAGE_ROTATION = 26;
const PRODUCT_IMAGE_WIDTH = 160;
const PRODUCT_IMAGE_TOP = -59;
const PRODUCT_IMAGE_RIGHT = -50;

// Fixed width reserved for the SKU code in the header, at BASE_WIDTH. All codes
// are 7 characters but their glyphs differ in width ("A01-BBI" vs "B04-AMU"),
// which would otherwise shift "Personalized / Probiotics" from card to card.
// Reserving a fixed column keeps the subtitle at the same x on every card.
const SKU_COLUMN_WIDTH = 115;

// Brand colour used when a SKU has no `color` in the facts config.
const DEFAULT_BRAND_COLOR = '#7a3f8f';

// Blend a hex colour towards another by `amount` (0..1). The card builds its
// header gradient and tinted active-strain box from a single brand colour.
const mixHex = (hex, target, amount) => {
  const parse = (h) => {
    const s = String(h).replace('#', '');
    const full = s.length === 3 ? s.split('').map((c) => c + c).join('') : s;
    return [0, 2, 4].map((i) => parseInt(full.slice(i, i + 2), 16));
  };
  const [r1, g1, b1] = parse(hex);
  const [r2, g2, b2] = parse(target);
  const ch = (a, b) => Math.round(a + (b - a) * amount);
  return `#${[ch(r1, r2), ch(g1, g2), ch(b1, b2)]
    .map((v) => v.toString(16).padStart(2, '0'))
    .join('')}`;
};

/**
 * A single "Supplement Facts" card for the new 9P formula, rendered as HTML/CSS
 * (not an image) so the text stays crisp and legible at any size.
 *
 * @param {string}  sku            e.g. 'A01-BBI'
 * @param {number}  width          rendered card width in px (font sizes scale with it)
 * @param {number}  height         optional fixed card height in px. The grid passes its
 *                                 row height so every card is exactly one row tall and
 *                                 a long ingredients list can't push the card past the
 *                                 bottom of the page. Omit for content-driven height.
 * @param {boolean} useThaiVersion swap the static labels to Thai
 */
const Supplement9PCard = ({ sku, width = BASE_WIDTH, height, useThaiVersion = false }) => {
  const data = newformula_facts_9P[sku] || {};
  const labels = useThaiVersion ? newformula_card_labels.thai : newformula_card_labels.eng;

  // Thai versions of the SKU code, strain, ingredients and serving size. Only
  // the strain is stored per SKU; the rest is derived (see the facts config).
  const skuLabel = useThaiVersion ? thai_sku_code(sku) : sku;
  const activeStrain = useThaiVersion
    ? thai_active_strain[sku] || data.activeStrain
    : data.activeStrain;
  const otherIngredients = useThaiVersion ? thai_other_ingredients : data.otherIngredients;
  const servingSize = useThaiVersion ? thai_serving_size : data.servingSize;

  // Swap measurement units to Thai, leaving the numbers untouched.
  const localizeUnit = (unit) => (useThaiVersion ? thai_units[unit] || unit : unit);

  // Scale factor: every px value below is multiplied by this.
  const s = width / BASE_WIDTH;
  const px = (n) => `${Math.round(n * s * 100) / 100}px`;

  const nutrition = data.nutrition || {};
  const val = (v) => (v && String(v).trim().length > 0 ? v : '—');

  // Split calories into a large value + small unit, like the reference card
  // (e.g. "4.1 Kcal" -> "4.1" big, "Kcal" small).
  const calMatch = String(nutrition.calories || '').match(/^\s*([\d.]+)\s*(.*)$/);
  const calValue = calMatch ? calMatch[1] : '';
  const calUnit = calMatch ? calMatch[2] : '';

  // Split "0.1 g" into number + unit so the number can be set larger than its
  // unit — that keeps the figure readable in a narrow single-row chip.
  const splitValue = (v) => {
    const m = String(v || '').match(/^\s*([\d.]+)\s*(.*)$/);
    return m ? { num: m[1], unit: localizeUnit(m[2]) } : { num: '', unit: '' };
  };

  // Nutrition chips in display order. Calories shows just the number, with its
  // unit carried by the chip label ("4.1" / "KCAL"); the rest keep their unit.
  const short = labels.short || {};
  const chips = [
    { label: short.calories || localizeUnit(calUnit) || 'Kcal', ...splitValue(calValue) },
    { label: short.protein, ...splitValue(nutrition.protein) },
    { label: short.totalFat, ...splitValue(nutrition.totalFat) },
    { label: short.totalCarbohydrate, ...splitValue(nutrition.totalCarbohydrate) },
    { label: short.sodium, ...splitValue(nutrition.sodium) }
  ];

  // Whole palette derives from the SKU's brand colour so each product keeps its
  // own identity while the layout stays identical.
  const BRAND = data.color || DEFAULT_BRAND_COLOR;
  const HEADER_FROM = mixHex(BRAND, '#000000', 0.13);
  const HEADER_TO = mixHex(BRAND, '#ffffff', 0.25);
  const ACCENT = mixHex(BRAND, '#000000', 0.08);
  const STRAIN_BG = mixHex(BRAND, '#ffffff', 0.9);
  const STRAIN_BORDER = mixHex(BRAND, '#ffffff', 0.78);
  const CARD_BORDER = mixHex(BRAND, '#ffffff', 0.82);
  const INK = '#2b2b2b';

  return (
    <div
      className="s9p-card"
      style={{
        width: px(BASE_WIDTH),
        // Hard-pin the width: every card must be identical regardless of how
        // long its strain name or ingredient words are.
        maxWidth: px(BASE_WIDTH),
        minWidth: 0,
        flex: 'none',
        // When the grid gives a row height, pin to it and let the body absorb
        // the remainder (see the body's flex/overflow below).
        ...(height ? { height: `${height}px`, display: 'flex', flexDirection: 'column' } : {}),
        overflowWrap: 'break-word',
        wordBreak: 'break-word',
        boxSizing: 'border-box',
        border: `${px(1.5)} solid ${CARD_BORDER}`,
        borderRadius: px(14),
        // Not clipped: the product stick deliberately overhangs the top edge.
        overflow: 'visible',
        background: '#ffffff',
        fontFamily: CARD_FONT,
        color: INK,
        lineHeight: 1.25
      }}
    >
      {/* Header ------------------------------------------------------------ */}
      <div
        style={{
          position: 'relative',
          background: `linear-gradient(135deg, ${HEADER_FROM} 0%, ${HEADER_TO} 100%)`,
          color: '#ffffff',
          padding: `${px(14)} ${px(12)}`,
          // Header carries its own top radius since the card no longer clips.
          borderTopLeftRadius: px(13),
          borderTopRightRadius: px(13),
          overflow: 'visible'
        }}
      >
        {/* SKU and the two-line "Personalized / Probiotics" share one row.
            Centred (not baseline) so both lines sit beside the SKU code. */}
        <div style={{ display: 'flex', alignItems: 'center', gap: px(7) }}>
          <span
            className="s9p-fw8"
            style={{
              // The transliterated Thai code is far longer than the Latin one
              // ("เอ01-บีบีไอ" vs "A01-BBI"), so it is set smaller and tighter
              // to stay inside the same fixed column.
              fontSize: useThaiVersion ? px(15) : px(22),
              fontWeight: 800,
              letterSpacing: useThaiVersion ? px(0) : px(1.6),
              whiteSpace: 'nowrap',
              // Fixed column so the subtitle never shifts between SKUs.
              flex: '0 0 auto',
              width: px(SKU_COLUMN_WIDTH)
            }}
          >
            {skuLabel}
          </span>
          {/* Each word on its own line: "Personalized" / "Probiotics" */}
          <span style={{ fontSize: px(8.5), fontStyle: 'italic', opacity: 0.95, lineHeight: 1.15 }}>
            {String(labels.subtitle || '')
              .split(/\s+/)
              .filter(Boolean)
              .map((word, i) => (
                <React.Fragment key={word + i}>
                  {i > 0 && <br />}
                  {word}
                </React.Fragment>
              ))}
          </span>
        </div>

        {/* Brand tagline */}
        <div
          className="s9p-fw6"
          style={{
            fontSize: px(6.5),
            fontWeight: 600,
            letterSpacing: px(0.9),
            textTransform: 'uppercase',
            opacity: 0.95,
            marginTop: px(6)
          }}
        >
          {labels.tagline}
        </div>

        {/* Product stick, tilted, overhanging the card's top-right corner */}
        <img
          src={`/supplement/newformula/${sku}.png`}
          alt=""
          style={{
            position: 'absolute',
            top: px(PRODUCT_IMAGE_TOP),
            right: px(PRODUCT_IMAGE_RIGHT),
            width: px(PRODUCT_IMAGE_WIDTH),
            height: 'auto',
            transform: `rotate(${PRODUCT_IMAGE_ROTATION}deg)`,
            transformOrigin: 'center center',
            pointerEvents: 'none',
            zIndex: 2
          }}
          onError={(e) => {
            e.target.style.display = 'none';
          }}
        />
      </div>

      {/* Body -------------------------------------------------------------- */}
      {/* Side padding is kept tight so the five nutrition chips fit one row. */}
      {/* On a fixed-height card the body takes the leftover space and clips, so
          the unbounded ingredients paragraph can't stretch the card. */}
      <div
        style={{
          padding: `${px(14)} ${px(12)} ${px(14)}`,
          ...(height ? { flex: 1, minHeight: 0, overflow: 'hidden' } : {})
        }}
      >
        {/* Active strain box */}
        <div
          style={{
            background: STRAIN_BG,
            border: `${px(1)} solid ${STRAIN_BORDER}`,
            borderRadius: px(8),
            padding: `${px(7)} ${px(9)}`,
            marginBottom: px(11)
          }}
        >
          <div
            className="s9p-fw7"
            style={{
              fontSize: px(7),
              fontWeight: 700,
              letterSpacing: px(1),
              color: ACCENT,
              textTransform: 'uppercase',
              marginBottom: px(2)
            }}
          >
            {labels.activeStrain}
          </div>
          <div
            className="s9p-fw7"
            style={{
              fontSize: px(12),
              fontWeight: 700,
              // Latin binomials are italicised by convention; Thai names are not.
              fontStyle: useThaiVersion ? 'normal' : 'italic',
              color: INK
            }}
          >
            {val(activeStrain)}
          </div>
        </div>

        {/* Supplement Facts header: title left, serving size right */}
        <div
          style={{
            display: 'flex',
            alignItems: 'baseline',
            justifyContent: 'space-between',
            gap: px(6),
            paddingBottom: px(3),
            borderBottom: `${px(1.5)} solid ${INK}`
          }}
        >
          <span className="s9p-fw8" style={{ fontSize: px(13), fontWeight: 800 }}>
            {labels.supplementFacts}
          </span>
          <span style={{ fontSize: px(9.5), color: '#8a7d95', whiteSpace: 'nowrap' }}>
            {labels.per} {val(servingSize)}
          </span>
        </div>

        {/* Nutrition chips — all five on a single row (value above, label below) */}
        <div
          style={{
            display: 'grid',
            gridTemplateColumns: 'repeat(5, 1fr)',
            gap: px(3.5),
            marginTop: px(6)
          }}
        >
          {chips.map((c) => (
            <div
              key={c.label}
              style={{
                background: '#f2f1f4',
                borderRadius: px(5),
                padding: `${px(4)} ${px(3)}`,
                textAlign: 'left'
              }}
            >
              <div
                className="s9p-fw8"
                style={{ fontSize: px(12), fontWeight: 800, color: INK, whiteSpace: 'nowrap' }}
              >
                {c.num || '—'}
                {c.unit && (
                  <span style={{ fontSize: px(8), fontWeight: 800, marginLeft: px(1) }}>{c.unit}</span>
                )}
              </div>
              <div
                className="s9p-fw7"
                style={{
                  fontSize: px(7),
                  fontWeight: 700,
                  letterSpacing: px(0.3),
                  color: '#9a94a3',
                  textTransform: 'uppercase',
                  marginTop: px(1),
                  whiteSpace: 'nowrap'
                }}
              >
                {c.label}
              </div>
            </div>
          ))}
        </div>

        {/* Other ingredients */}
        <div style={{ marginTop: px(12) }}>
          <div
            className="s9p-fw7"
            style={{
              fontSize: px(9),
              fontWeight: 700,
              letterSpacing: px(1.2),
              color: '#8a7d95',
              textTransform: 'uppercase',
              marginBottom: px(4)
            }}
          >
            {labels.otherIngredients}
          </div>
          <div style={{ fontSize: px(11), color: '#7a6a85', lineHeight: 1.35 }}>
            {val(otherIngredients)}
          </div>
        </div>
      </div>
    </div>
  );
};

export default Supplement9PCard;
